import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import UserGroupChip from './UserGroupChip';
import UserGroupSwitcherMenu from './UserGroupSwitcherMenu';
import { GLOBAL_GROUP_LABEL } from './userGroupConstants';
import useUnsavedWorkGuard from 'src/components/pedigree-chart/workspace/useUnsavedWorkGuard';
import { agreeToUnloadWithUnsavedWork } from 'src/store/workspace/unsavedWork';

// Wrapper around the chip + menu. Owns:
//   - The anchorEl state that drives the dropdown.
//   - The reload-after-switch network calls (matches the legacy switcher's
//     pattern; full-page reload picks up the new JWT cleanly).
//   - The combined chip+avatar visual cluster — the avatar (rendered by the
//     caller, passed via `avatarSlot`) sits on top of the chip's right edge.
const UserGroupChipCluster = ({
  user,
  faviconUrl,
  navbarColor,
  accentColor,
  avatarSlot
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  // Every action here ends in a page reload, which drops the whole workspace
  // and any section edits still unsaved in its tabs. It used to lean on the
  // save footer's native beforeunload prompt, but that only fires at the
  // reload, AFTER the switch has been posted: Cancel there left the page
  // showing the old group under the new group's session. So it asks first,
  // before anything is sent, and the native prompt then stands down for a
  // reload the clinician has already agreed to.
  const { requestLeave, leaveDialog } = useUnsavedWorkGuard();
  const reload = () => {
    agreeToUnloadWithUnsavedWork();
    window.location.reload();
  };

  const groups = useMemo(
    () => (Array.isArray(user?.groups) ? user.groups : []),
    [user]
  );
  const isMultiGroup = groups.length > 1;

  const currentGroupId = useMemo(() => {
    const raw = user?.userGroup;
    if (!raw) return null;
    if (typeof raw === 'object' && raw._id) return raw._id.toString();
    return raw.toString();
  }, [user]);

  const defaultGroupId = useMemo(() => {
    const raw = user?.selectedUserGroup;
    if (!raw) return null;
    if (typeof raw === 'object' && raw._id) return raw._id.toString();
    return raw.toString();
  }, [user]);

  // The active group's label lives on user.groups[].userGroup.label — the
  // top-level user.userGroup is often just an ObjectId reference.
  const activeGroupEntry = useMemo(
    () => groups.find((g) => g.userGroup?._id?.toString() === currentGroupId),
    [groups, currentGroupId]
  );

  // A System Admin is in no clinic group: their group is a sentinel with no
  // usergroups document, so it has no name to show. The chip says "Global"
  // with a globe instead (TGCLOUD-1858), and there is nowhere to switch to.
  const isGlobal =
    user?.userType === CONSTANTS.userTypes.ADMIN &&
    !activeGroupEntry?.userGroup?.label;

  if (!activeGroupEntry && !isGlobal) return avatarSlot || null;

  // The chip's "User Group" shape combines the populated UserGroup doc with
  // the user's per-group userType and the active-group favicon resolved by
  // ThemeContext (set after auth — see Dashboard.jsx).
  const chipUserGroup = isGlobal
    ? {
        _id: currentGroupId,
        label: GLOBAL_GROUP_LABEL,
        userType: CONSTANTS.userTypes.ADMIN,
        global: true,
        faviconUrl: null
      }
    : {
        _id: activeGroupEntry.userGroup?._id,
        label: activeGroupEntry.userGroup?.label,
        userType: activeGroupEntry.userType,
        faviconUrl: faviconUrl || null
      };
  const canSwitch = isMultiGroup && !isGlobal;

  const roleFullName =
    CONSTANTS.userTypeOptionsMap?.[chipUserGroup.userType] ||
    chipUserGroup.userType ||
    '';

  const handleOpen = (e) => {
    if (!canSwitch) return;
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSwitch = (userGroupId, isCurrent) => {
    if (isCurrent) {
      handleClose();
      return;
    }
    handleClose();
    requestLeave(
      async () => {
        try {
          await axios.post(APICONSTANTS.switchUserGroup, { userGroupId });
          reload();
        } catch (err) {
          console.error('Failed to switch User Group', err);
        }
      },
      { confirmLabel: 'Discard and switch group' }
    );
  };

  const handleSetDefault = (userGroupId) => {
    handleClose();
    requestLeave(
      async () => {
        try {
          await axios.post(APICONSTANTS.switchUserGroup, {
            userGroupId,
            setAsDefault: true
          });
          reload();
        } catch (err) {
          console.error('Failed to set default User Group', err);
        }
      },
      { confirmLabel: 'Discard and reload' }
    );
  };

  const handleClearDefault = () => {
    handleClose();
    requestLeave(
      async () => {
        try {
          await axios.post(APICONSTANTS.clearDefaultUserGroup);
          reload();
        } catch (err) {
          console.error('Failed to clear default User Group', err);
        }
      },
      { confirmLabel: 'Discard and reload' }
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <UserGroupChip
        userGroup={chipUserGroup}
        roleFullName={roleFullName}
        isMultiGroup={canSwitch}
        isMenuOpen={Boolean(anchorEl)}
        onClick={handleOpen}
      />
      {/* The avatar overlaps the chip's right edge. z-index + a box-shadow
          matching the navbar colour masks the chip's square right corner
          behind the avatar's circle. */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          borderRadius: '50%',
          boxShadow: `0 0 0 1.5px ${navbarColor || 'transparent'}`,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {avatarSlot}
      </Box>
      {canSwitch && (
        <UserGroupSwitcherMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          groups={groups}
          currentGroupId={currentGroupId}
          defaultGroupId={defaultGroupId}
          accentColor={accentColor}
          onSwitch={handleSwitch}
          onSetDefault={handleSetDefault}
          onClearDefault={handleClearDefault}
        />
      )}
      {leaveDialog}
    </Box>
  );
};

export default UserGroupChipCluster;
